// Typed API client.
//
// Two behaviours here are load-bearing and easy to lose in a refactor:
//
//  1. **Refresh-on-401 with a single in-flight refresh.** Access tokens are
//     short-lived, so any page can hit a 401 at any time. Without this the
//     user is bounced to the login screen mid-session — the logout problem
//     this migration set out to fix. Concurrent 401s share one refresh call;
//     firing several would rotate the refresh token repeatedly and invalidate
//     the very token the other callers are waiting on.
//
//  2. **Refresh failure is the only path that clears the session.** A network
//     blip must not log the user out, so only an explicit rejection from the
//     refresh endpoint counts as "really signed out".

const TOKEN_STORAGE_KEY = "lf.auth";

/**
 * Origin the API is served from.
 *
 * In development, and in the tests, this is empty: requests stay relative and
 * Vite's proxy forwards `/api` to the local worker, so the browser sees one
 * origin. In production the SPA is static files on a CDN and the API is a
 * separate Edge Script on its own hostname, so a relative URL would resolve
 * against the CDN and 404 — which is why this is configurable rather than
 * assumed.
 *
 * Set at build time via `VITE_API_ORIGIN`. Any trailing slash is stripped so
 * it always concatenates cleanly with a leading-slash path.
 */
const API_ORIGIN = (import.meta.env?.VITE_API_ORIGIN ?? "").replace(/\/$/, "");

/**
 * True when the app is served from somewhere other than the API. A production
 * build with no origin configured is a deployment mistake: every request would
 * silently resolve against the CDN, which answers with its own 404/405 pages.
 * That failure is especially misleading on the auth callback, where a 405 is
 * indistinguishable from a rejected token and surfaces as "link expired".
 */
const REQUIRES_ORIGIN = import.meta.env?.PROD === true;

if (REQUIRES_ORIGIN && API_ORIGIN === "") {
  throw new Error(
    "VITE_API_ORIGIN is not set. A production build must be given the API origin at " +
      "build time, or every request resolves against the CDN instead of the API.",
  );
}

/** Resolves an API path against the configured origin. */
export function apiUrl(path: string): string {
  return `${API_ORIGIN}${path}`;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Thrown when the session cannot be recovered and the user must sign in again. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, "SESSION_EXPIRED", "Your session has expired. Please sign in again.");
    this.name = "SessionExpiredError";
  }
}

function readTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthTokens>;
    if (!parsed.accessToken || !parsed.refreshToken) return null;
    return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken };
  } catch {
    // Corrupt storage should present as "signed out", not a crash on boot.
    return null;
  }
}

function writeTokens(tokens: AuthTokens | null): void {
  if (tokens) localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(TOKEN_STORAGE_KEY);
}

type Listener = (tokens: AuthTokens | null) => void;
const listeners = new Set<Listener>();

/** Subscribe to sign-in/sign-out. Used by the auth context to re-render. */
export function onAuthChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setTokens(tokens: AuthTokens | null): void {
  writeTokens(tokens);
  for (const listener of listeners) listener(tokens);
}

export function getTokens(): AuthTokens | null {
  return readTokens();
}

export function isSignedIn(): boolean {
  return readTokens() !== null;
}

/**
 * Shared across concurrent 401s so only one refresh is ever in flight. The
 * refresh endpoint rotates the token, so parallel refreshes would race and
 * invalidate each other.
 */
let refreshInFlight: Promise<AuthTokens | null> | null = null;

async function refreshTokens(): Promise<AuthTokens | null> {
  const current = readTokens();
  if (!current) return null;

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(apiUrl("/api/v1/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });

      if (!res.ok) {
        // Only an explicit rejection means the session is unrecoverable. A
        // 5xx or network failure is transient and must not sign the user out.
        if (res.status === 401 || res.status === 422) setTokens(null);
        return null;
      }

      const next = (await res.json()) as AuthTokens;
      setTokens(next);
      return next;
    } catch {
      // Network error: keep the session and let the caller surface the failure.
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skips the Authorization header and the refresh retry. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function parseError(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as
    | { error?: string; message?: string }
    | null;
  return new ApiError(
    res.status,
    body?.error ?? "UNKNOWN",
    body?.message ?? res.statusText ?? "Request failed",
  );
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, anonymous = false, signal } = options;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;

    return fetch(apiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  const tokens = anonymous ? null : readTokens();
  let res = await send(tokens?.accessToken ?? null);

  if (res.status === 401 && !anonymous && tokens) {
    const refreshed = await refreshTokens();
    if (!refreshed) throw new SessionExpiredError();
    res = await send(refreshed.accessToken);
  }

  if (!res.ok) throw await parseError(res);

  // 204 and empty bodies are valid responses for deletes and some mutations.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Auth flow
// ---------------------------------------------------------------------------

export async function requestMagicLink(email: string): Promise<void> {
  await apiRequest<{ message: string }>("/api/v1/auth/request-link", {
    method: "POST",
    body: { email },
    anonymous: true,
  });
}

export async function verifyMagicLink(token: string): Promise<void> {
  const tokens = await apiRequest<AuthTokens>("/api/v1/auth/verify", {
    method: "POST",
    body: { token },
    anonymous: true,
  });
  setTokens(tokens);
}

export async function signOut(): Promise<void> {
  const tokens = readTokens();
  // Clear locally first: the user asked to sign out, so it must appear to
  // succeed even if the network call fails.
  setTokens(null);
  if (!tokens) return;
  await fetch(apiUrl("/api/v1/auth/logout"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  }).catch(() => undefined);
}
